import { useState } from "react";
import { GBP, ukDateLong, ymd, priceLabel, DAYS } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Clock, ChevronRight, MapPin, Phone, Star, Scissors, ShieldCheck, Search, Mail, CalendarDays } from "lucide-react";

const img = (u) => (!u ? null : (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

function Stars({ n, className = "h-4 w-4" }) {
  return <div className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${className} ${i <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-white/40"}`} />)}</div>;
}

export function SalonHero({ biz, reviewData, accent, onBook }) {
  const br = biz.branding || {};
  const cover = img(biz.cover_url) || img((biz.gallery || [])[0]);
  const logo = img(biz.logo_url);
  const script = br.logo_style === "script" && !logo;
  return (
    <section data-testid="public-header" className="relative isolate overflow-hidden text-white" style={{ background: br.primary || "#1c1917" }}>
      {cover && <img src={cover} alt="" data-testid="public-cover" className="absolute inset-0 -z-10 h-full w-full object-cover" />}
      <div className="absolute inset-0 -z-10" style={{ background: cover ? "linear-gradient(90deg, rgba(12,10,9,.82) 0%, rgba(12,10,9,.55) 55%, rgba(12,10,9,.25) 100%)" : `linear-gradient(135deg, ${br.primary || "#1c1917"}, ${accent})` }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16 lg:py-24">
        <div className="max-w-2xl animate-fade-up">
          {logo && <div className="h-16 w-16 lg:h-20 lg:w-20 rounded-2xl overflow-hidden ring-2 ring-white/30 mb-5"><img src={logo} alt="" className="h-full w-full object-cover" /></div>}
          {biz.tagline && <p className="text-xs tracking-[0.3em] uppercase font-semibold text-white/80">{biz.tagline}</p>}
          <h1 data-testid="public-business-name" className={script ? "font-script text-5xl sm:text-6xl lg:text-7xl leading-tight mt-2" : "font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tight"}>{biz.name}</h1>
          {reviewData.count > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm"><Stars n={reviewData.average} /><span className="font-semibold">{reviewData.average}</span><span className="text-white/70">({reviewData.count} review{reviewData.count === 1 ? "" : "s"})</span></div>
          )}
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/85">
            {biz.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(biz.address)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:underline"><MapPin className="h-4 w-4" />{biz.address}</a>}
            {biz.phone && <a href={`tel:${biz.phone}`} className="flex items-center gap-1.5 hover:underline"><Phone className="h-4 w-4" />{biz.phone}</a>}
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <button data-testid="hero-book-button" onClick={onBook} className="h-12 px-7 rounded-full font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: accent }}>Book an appointment</button>
            {biz.phone && <a href={`tel:${biz.phone}`} className="h-12 px-6 rounded-full font-semibold border border-white/40 bg-white/10 backdrop-blur-md flex items-center gap-2 hover:bg-white/20 transition-colors"><Phone className="h-4 w-4" />Call</a>}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceRow({ s, accent, onPick }) {
  return (
    <button data-testid={`public-booking-service-select-${s.id}`} onClick={() => onPick(s)}
      className="w-full py-3.5 px-1 text-left flex items-center gap-3 group border-t border-stone-100 first:border-t-0">
      {s.image_url && <div className="h-12 w-12 rounded-xl overflow-hidden shrink-0"><img src={img(s.image_url)} alt="" className="h-full w-full object-cover" /></div>}
      <div className="flex-1 min-w-0">
        <div className="font-medium leading-tight group-hover:underline underline-offset-4">{s.name}</div>
        {s.description && <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">{s.description}</div>}
        <div className="text-xs text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration_min} min</span>
          {s.deposit > 0 && <span className="text-sky-700">Deposit {GBP(s.deposit)}</span>}
          {(s.requires_consent || s.requires_patch_test || s.requires_guardian) && <span className="flex items-center gap-0.5" style={{ color: accent }}><ShieldCheck className="h-3 w-3" />{s.requires_patch_test ? "Patch test needed" : s.requires_guardian ? "Guardian consent" : "Consent form"}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono font-semibold text-sm">{priceLabel(s)}</div>
        <div className="text-xs font-semibold mt-0.5 flex items-center justify-end gap-0.5" style={{ color: accent }}>Book<ChevronRight className="h-3.5 w-3.5" /></div>
      </div>
    </button>
  );
}

export function ServiceMenu({ services, accent, onPick }) {
  const [q, setQ] = useState("");
  const cats = [...new Set(services.map((s) => s.category || "General"))];
  const term = q.trim().toLowerCase();
  const matches = (s) => !term || s.name.toLowerCase().includes(term) || (s.category || "").toLowerCase().includes(term);
  const shown = cats.filter((c) => services.some((s) => (s.category || "General") === c && matches(s)));
  return (
    <section id="services" data-testid="public-service-menu" className="scroll-mt-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
        <div><h2 className="font-extrabold text-2xl sm:text-3xl tracking-tight">Treatments & prices</h2>
          <p className="text-sm text-stone-500 mt-1">{services.length} treatments in {cats.length} categories. Tap a category to open it.</p></div>
        <div className="relative sm:w-64">
          <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="service-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search treatments" className="pl-9 h-11 rounded-full bg-white" />
        </div>
      </div>
      {shown.length === 0 && <p className="text-sm text-stone-500" data-testid="service-search-empty">No treatments match "{q}".</p>}
      <Accordion type="multiple" key={term ? `search-${term}` : "browse"} defaultValue={term ? shown : []} className="space-y-2.5">
        {shown.map((cat) => {
          const list = services.filter((s) => (s.category || "General") === cat && matches(s));
          const from = Math.min(...list.map((s) => s.price));
          return (
            <AccordionItem key={cat} value={cat} data-testid={`service-category-${cat}`} className="bg-white rounded-2xl border border-stone-200 shadow-sm px-4 sm:px-5 border-b-0">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}18`, color: accent }}><Scissors className="h-4 w-4" /></div>
                  <div><div className="font-semibold">{cat}</div><div className="text-xs text-stone-500 font-normal">{list.length} treatment{list.length === 1 ? "" : "s"} · from {GBP(from)}</div></div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-2">{list.map((s) => <ServiceRow key={s.id} s={s} accent={accent} onPick={onPick} />)}</AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </section>
  );
}

export function InfoPanel({ biz, accent, onBook }) {
  const h = biz.opening_hours || {};
  const today = (new Date().getDay() + 6) % 7;
  const specials = Object.keys(biz.special_dates || {}).filter((d) => d >= ymd(new Date())).sort().slice(0, 4);
  return (
    <aside className="space-y-4 lg:sticky lg:top-6" data-testid="public-info-panel">
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
        <h3 className="font-semibold flex items-center gap-2"><Clock className="h-4 w-4" style={{ color: accent }} />Opening hours</h3>
        <dl className="mt-3 text-sm space-y-1.5" data-testid="public-hours">
          {DAYS.map((day, d) => {
            const o = h[d] || h[String(d)];
            const ev = o?.evening?.on ? `, ${o.evening.start}–${o.evening.end}` : "";
            return (
              <div key={day} className={`flex justify-between gap-3 ${d === today ? "font-semibold" : ""}`}>
                <dt className="text-stone-600">{day}</dt>
                <dd className={!o || o.closed ? "text-stone-400" : "font-mono text-xs sm:text-sm"}>{!o || o.closed ? "Closed" : `${o.open}–${o.close}${ev}`}</dd>
              </div>
            );
          })}
        </dl>
        {specials.length > 0 && <p className="text-xs text-stone-500 mt-3">Also open {specials.map((d) => ukDateLong(d).replace(/ \d{4}$/, "")).join(", ")}</p>}
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 text-sm space-y-2.5">
        <h3 className="font-semibold">Find us</h3>
        {biz.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(biz.address)}`} target="_blank" rel="noreferrer" className="flex gap-2 text-stone-700 hover:underline"><MapPin className="h-4 w-4 shrink-0 mt-0.5" />{biz.address}</a>}
        {biz.phone && <a href={`tel:${biz.phone}`} className="flex gap-2 text-stone-700 hover:underline"><Phone className="h-4 w-4 shrink-0 mt-0.5" />{biz.phone}</a>}
        {biz.email && <a href={`mailto:${biz.email}`} className="flex gap-2 text-stone-700 hover:underline break-all"><Mail className="h-4 w-4 shrink-0 mt-0.5" />{biz.email}</a>}
        <button data-testid="info-book-button" onClick={onBook} className="mt-2 w-full h-11 rounded-full font-semibold text-white flex items-center justify-center gap-2" style={{ background: accent }}><CalendarDays className="h-4 w-4" />Book now</button>
      </div>
      {biz.description && <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 text-sm"><h3 className="font-semibold mb-1.5">About us</h3><p className="text-stone-600 whitespace-pre-line">{biz.description}</p></div>}
    </aside>
  );
}
